import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { collegeJourneyProgressService } from '../../../lib/college-journey-service';
import { Sparkles, RefreshCw, CheckCircle, AlertCircle, Target, Brain } from 'lucide-react';

const Step3_StrategicContextPreview = ({ wizardData, updateData, currentUser, goToNextStep }) => {
  const { profile, parentInsights, strategicContext, selectedAccount } = wizardData;
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Save profile and parent insights to database first
  const saveProfileData = async () => {
    try {
      const userId = selectedAccount?.auth_user_id || selectedAccount?.id;

      const profileData = {
        user_id: userId,
        name: profile.name,
        graduation_year: profile.graduation_year,
        grade_level: profile.grade_level,
        state_of_residency: profile.state_of_residency,
        intended_major: profile.intended_major,
        intended_minor: profile.intended_minor,
        gpa_weighted: profile.gpa_weighted,
        gpa_unweighted: profile.gpa_unweighted,
        gpa_scale: profile.gpa_scale,
        class_rank: profile.class_rank,
        class_size: profile.class_size,
        sat_total: profile.sat_total,
        sat_math: profile.sat_math,
        sat_reading_writing: profile.sat_reading_writing,
        act_composite: profile.act_composite,
        act_math: profile.act_math,
        act_english: profile.act_english,
        act_reading: profile.act_reading,
        act_science: profile.act_science,
        curriculum_rigor: profile.curriculum_rigor,
        ap_courses_taken: Array.isArray(profile.ap_courses_taken) ? profile.ap_courses_taken.length : (profile.ap_courses_taken || 0),
        honors_courses: Array.isArray(profile.honors_courses_taken) ? profile.honors_courses_taken.length : (profile.honors_courses || 0),
        dual_enrollment_credits: profile.dual_enrollment_credits || 0,
        career_goals: profile.career_goals,
        special_talents: profile.special_talents,
        high_school_name: profile.high_school_name,
        high_school_city: profile.high_school_city,
        high_school_state: profile.high_school_state,
        extracurriculars: profile.extracurriculars || [],
        awards: profile.honors || [],
        parent_observation_easy_subject: parentInsights.parentA?.easy_subject,
        parent_observation_growth: parentInsights.parentA?.growth,
        parent_observation_learning_difference: parentInsights.parentA?.learning_difference,
        parent_observation_surprises: parentInsights.parentA?.surprises,
        parent_observation_age_limit: parentInsights.parentA?.age_limit,
        parent_observation_rules_questioned: parentInsights.parentA?.rules_questioned,
        parent_observation_wrong_level: parentInsights.parentA?.wrong_level,
        parent_observation_advocacy_success: parentInsights.parentA?.advocacy_success,
        parent_observation_initiative_against_advice: parentInsights.parentA?.initiative_against_advice,
        parent_observation_mechanical_skills: parentInsights.parentA?.mechanical_skills,
        parent_observation_math_integration: parentInsights.parentA?.math_integration,
        parent_observation_entrepreneurial_instincts: parentInsights.parentA?.entrepreneurial_instincts,
        parent_observation_obsessions: parentInsights.parentA?.obsessions,
        parent_observation_unusual_interests: parentInsights.parentA?.unusual_interests,
        parent_observation_hidden_qualities: parentInsights.parentA?.hidden_qualities,
        parent_b_easy_subject: parentInsights.parentB?.easy_subject,
        parent_b_growth: parentInsights.parentB?.growth,
        parent_b_learning_difference: parentInsights.parentB?.learning_difference,
        parent_b_surprises: parentInsights.parentB?.surprises,
        parent_b_age_limit: parentInsights.parentB?.age_limit,
        parent_b_rules_questioned: parentInsights.parentB?.rules_questioned,
        parent_b_wrong_level: parentInsights.parentB?.wrong_level,
        parent_b_advocacy_success: parentInsights.parentB?.advocacy_success,
        parent_b_initiative_against_advice: parentInsights.parentB?.initiative_against_advice,
        parent_b_mechanical_skills: parentInsights.parentB?.mechanical_skills,
        parent_b_math_integration: parentInsights.parentB?.math_integration,
        parent_b_entrepreneurial_instincts: parentInsights.parentB?.entrepreneurial_instincts,
        parent_b_obsessions: parentInsights.parentB?.obsessions,
        parent_b_unusual_interests: parentInsights.parentB?.unusual_interests,
        parent_b_hidden_qualities: parentInsights.parentB?.hidden_qualities,
        parent_b_different_perspective: parentInsights.parentB?.different_perspective,
        parent_b_what_other_parent_misses: parentInsights.parentB?.what_other_parent_misses,
        updated_at: new Date().toISOString()
      };

      // Check if profile already exists for this user
      const { data: existing } = await supabase
        .from('student_academic_profiles')
        .select('id')
        .eq('user_id', userId)
        .single();

      let data, error;

      if (existing) {
        // Update existing profile
        const result = await supabase
          .from('student_academic_profiles')
          .update(profileData)
          .eq('user_id', userId)
          .select()
          .single();
        data = result.data;
        error = result.error;
        console.log('✅ Updated existing profile for user:', userId);
      } else {
        // Insert new profile
        const result = await supabase
          .from('student_academic_profiles')
          .insert(profileData)
          .select()
          .single();
        data = result.data;
        error = result.error;
        console.log('✅ Created new profile for user:', userId);
      }

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error saving profile data:', err);
      throw err;
    }
  };

  // Generate strategic context using the existing flow
  const generateStrategicContext = async () => {
    setGenerating(true);
    setError(null);

    try {
      // First save the profile and parent insights
      setSaving(true);
      const savedProfile = await saveProfileData();
      setSaving(false);

      console.log('🧠 Generating strategic context with AI...');

      // Generate strategic context prompt
      const contextPrompt = `Generate a comprehensive strategic context summary for this student:

**STUDENT PROFILE:**
Name: ${profile.name}
Graduation Year: ${profile.graduation_year}
Grade Level: ${profile.grade_level || '12th'}
Intended Major: ${profile.intended_major}
GPA: ${profile.gpa_weighted || profile.gpa_unweighted}
Academic Interests: ${Array.isArray(profile.academic_interests) ? profile.academic_interests.join(', ') : 'Not specified'}
Career Goals: ${profile.career_goals || 'Not specified'}
Special Talents: ${profile.special_talents || 'Not specified'}
Activities: ${Array.isArray(profile.activities) ? profile.activities.map(a => a.name || a).join(', ') : 'Not specified'}

**PARENT A OBSERVATIONS:**
${Object.entries(parentInsights.parentA || {}).map(([key, value]) => `${key}: ${value}`).join('\n')}

**PARENT B OBSERVATIONS:**
${Object.entries(parentInsights.parentB || {}).map(([key, value]) => `${key}: ${value}`).join('\n')}

**TASK:**
Create a comprehensive strategic context summary (500-800 words) that:
1. Synthesizes the student's academic profile with parent observations
2. Identifies key patterns, strengths, and unique qualities
3. Suggests positioning strategies for college applications
4. Highlights what makes this student distinctive

Return a detailed narrative summary that will inform personalized college guidance.`;

      // Call Claude API with higher token limit
      const response = await fetch('/api/anthropic/mock-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-followup-answers', // Reuse this action for text generation
          prompt: contextPrompt,
          model: 'claude-sonnet-4-20250514',
          max_tokens: 6000 // Increased token limit for comprehensive context
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const result = await response.json();
      const contextSummary = result.response;

      // Save strategic context to database
      const { error: updateError } = await supabase
        .from('student_academic_profiles')
        .update({
          strategic_context_summary: contextSummary,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', savedProfile.user_id);

      if (updateError) throw updateError;

      // Mark onboarding as complete so the student can access the full College Journey
      try {
        await collegeJourneyProgressService.markOnboardingComplete(savedProfile.user_id);
        console.log('✅ Onboarding marked complete for test profile');
      } catch (onboardingError) {
        console.warn('⚠️ Could not mark onboarding complete:', onboardingError);
        // Don't throw - profile was still saved successfully
      }

      const generatedContext = {
        summary: contextSummary,
        personalityProfile: {
          motivationStyle: 'achievement',
          communicationPreference: 'data-logic',
          resistancePatterns: 'perfectionist-paralyzed',
          energySource: 'independent-exploration',
          decisionMaking: 'research-heavy',
          feedbackStyle: 'direct-analytical',
          identityFramework: 'individual-achiever'
        },
        academicFocus: {
          focusArea: profile.intended_major?.toLowerCase() || 'general',
          positioningNarrative: 'institutional_catalyst',
          assessmentTemplate: 'default'
        }
      };

      updateData('strategicContext', generatedContext);
      console.log('✅ Strategic context generated and saved');
    } catch (err) {
      console.error('Error generating strategic context:', err);
      setError(`Failed to generate strategic context: ${err.message}`);
    } finally {
      setGenerating(false);
      setSaving(false);
    }
  };

  const hasContext = strategicContext && strategicContext.summary;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Strategic Context Generation</h2>
        <p className="text-slate-600">
          Generate a strategic context summary that combines the student profile with parent insights.
        </p>
      </div>

      {!hasContext && (
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Generate Strategic Context</h3>
          <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
            This will create a comprehensive strategic context summary, personality profile, and academic positioning for {profile.name}.
          </p>
          <button
            onClick={generateStrategicContext}
            disabled={generating || saving}
            className="px-8 py-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
          >
            {generating || saving ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                {saving ? 'Saving Profile...' : 'Generating Context...'}
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Strategic Context
              </>
            )}
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-red-900 mb-1">Generation Failed</h4>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {hasContext && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-emerald-900 mb-1">Strategic Context Generated!</h4>
              <p className="text-sm text-emerald-700">
                Review the strategic context below before proceeding to question generation.
              </p>
            </div>
            <button
              onClick={generateStrategicContext}
              disabled={generating}
              className="text-emerald-600 hover:text-emerald-800 flex items-center gap-2 text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              Regenerate
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5 text-emerald-600" />
              Strategic Context Summary
            </h3>
            <div className="prose prose-sm max-w-none">
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{strategicContext.summary}</p>
            </div>
          </div>

          {/* Only show personality/academic focus if they exist (from fresh generation) */}
          {(strategicContext.personalityProfile || strategicContext.academicFocus) && (
            <div className="grid md:grid-cols-2 gap-6">
              {strategicContext.personalityProfile && (
                <div className="bg-white border border-slate-200 rounded-lg p-6">
                  <h4 className="font-semibold text-slate-900 mb-4">Personality Profile</h4>
                  <div className="space-y-2 text-sm">
                    {Object.entries(strategicContext.personalityProfile).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-slate-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                        <span className="font-medium text-slate-900">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {strategicContext.academicFocus && (
                <div className="bg-white border border-slate-200 rounded-lg p-6">
                  <h4 className="font-semibold text-slate-900 mb-4">Academic Focus</h4>
                  <div className="space-y-2 text-sm">
                    {Object.entries(strategicContext.academicFocus).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-slate-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                        <span className="font-medium text-slate-900">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-azure-900 mb-1">Next Step: Question Set Generation</h4>
                <p className="text-sm text-azure-700">
                  This strategic context will be used to generate personalized assessment questions (1-8) for the student.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step3_StrategicContextPreview;
